import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { RESOLVED_THEMES, SYSTEM_THEME_QUERY } from "./theme.constants";
import {
  getNextThemePreference,
  getResolvedTheme,
} from "./themeResolver";
import {
  readThemePreference,
  storeThemePreference,
} from "./themeStorage";

const ThemeModeContext = createContext(null);

const getSystemDarkPreference = () =>
  window.matchMedia?.(SYSTEM_THEME_QUERY).matches ?? false;

const subscribeToSystemTheme = (handleChange) => {
  const mediaQuery = window.matchMedia?.(SYSTEM_THEME_QUERY);
  if (!mediaQuery) return undefined;

  mediaQuery.addEventListener("change", handleChange);
  return () => mediaQuery.removeEventListener("change", handleChange);
};

const createMuiTheme = (resolvedTheme) =>
  createTheme({
    palette: {
      mode: resolvedTheme,
      primary: {
        main: resolvedTheme === RESOLVED_THEMES.DARK ? "#8bbcff" : "#20497f",
      },
      background: {
        default: "var(--color-page)",
        paper: "var(--color-surface)",
      },
      text: {
        primary: "var(--color-text)",
        secondary: "var(--color-text-muted)",
      },
    },
    typography: {
      fontFamily: "Montserrat, sans-serif",
    },
  });

export const ThemeModeProvider = ({ children }) => {
  const [themePreference, setThemePreferenceState] = useState(readThemePreference);
  const [isSystemDark, setIsSystemDark] = useState(getSystemDarkPreference);
  const resolvedTheme = getResolvedTheme({ isSystemDark, themePreference });
  const muiTheme = useMemo(() => createMuiTheme(resolvedTheme), [resolvedTheme]);

  useEffect(() => {
    const handleChange = (event) => setIsSystemDark(event.matches);
    return subscribeToSystemTheme(handleChange);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  const setThemePreference = useCallback((nextPreference) => {
    setThemePreferenceState(nextPreference);
    storeThemePreference(nextPreference);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemePreference(getNextThemePreference(resolvedTheme));
  }, [resolvedTheme, setThemePreference]);

  const value = useMemo(
    () => ({ resolvedTheme, setThemePreference, themePreference, toggleTheme }),
    [resolvedTheme, setThemePreference, themePreference, toggleTheme]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const themeMode = useContext(ThemeModeContext);

  if (!themeMode) {
    throw new Error("useThemeMode must be used within ThemeModeProvider");
  }

  return themeMode;
};
